import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { addDays, addMonths, addYears } from "date-fns";
import type { documents_UploadSubsidyDocumentRequest } from "@/api/models/documents_UploadSubsidyDocumentRequest";

/**
 * The seven subsidy document categories of a contract version (issue #2002),
 * in the order of the paper trail. The backend allowlist is
 * models.SubsidyDocumentCategories (internal/models/document.go).
 */
export const SUBSIDY_DOCUMENT_CATEGORIES = [
  "bf_confirmation",
  "sbu_confirmation",
  "finanzierungsantrag",
  "elternvereinbarung",
  "subsidy_decision",
  "kmbb_fachstelle",
  "kmbb_standortgespraech",
] as const;

export type SubsidyDocumentCategory = (typeof SUBSIDY_DOCUMENT_CATEGORIES)[number];

// Compile-time guard: the list matches the generated request enum (the backend
// oneof), in both directions.
type GeneratedCategory = `${documents_UploadSubsidyDocumentRequest.category}`;
const _sameCategories: [SubsidyDocumentCategory, GeneratedCategory] extends [GeneratedCategory, SubsidyDocumentCategory]
  ? true
  : never = true;
void _sameCategories;

export function isSubsidyDocumentCategory(value: string): value is SubsidyDocumentCategory {
  return (SUBSIDY_DOCUMENT_CATEGORIES as readonly string[]).includes(value);
}

export const SUBSIDY_DOCUMENT_CATEGORY_LABELS: Record<SubsidyDocumentCategory, MessageDescriptor> = {
  bf_confirmation: msg({ id: "subsidyDocuments.category.bfConfirmation", message: "BF confirmation" }),
  sbu_confirmation: msg({ id: "subsidyDocuments.category.sbuConfirmation", message: "SBU confirmation" }),
  finanzierungsantrag: msg({ id: "subsidyDocuments.category.finanzierungsantrag", message: "Signed funding application" }),
  elternvereinbarung: msg({ id: "subsidyDocuments.category.elternvereinbarung", message: "Parent agreement" }),
  subsidy_decision: msg({ id: "subsidyDocuments.category.subsidyDecision", message: "City decision" }),
  kmbb_fachstelle: msg({ id: "subsidyDocuments.category.kmbbFachstelle", message: "KmbB specialist confirmation" }),
  kmbb_standortgespraech: msg({ id: "subsidyDocuments.category.kmbbStandortgespraech", message: "KmbB review meeting record" }),
};

/**
 * How long a document of an expiring category stays valid from its date: the
 * BF and SBU confirmations one year, the KmbB specialist confirmation at most
 * six months, the review meeting record at most two months. Categories
 * without an entry carry no validity dates.
 */
const VALIDITY: Partial<Record<SubsidyDocumentCategory, (from: Date) => Date>> = {
  bf_confirmation: (from) => addDays(addYears(from, 1), -1),
  sbu_confirmation: (from) => addDays(addYears(from, 1), -1),
  kmbb_fachstelle: (from) => addMonths(from, 6),
  kmbb_standortgespraech: (from) => addMonths(from, 2),
};

export function subsidyDocumentExpires(category: string): boolean {
  return isSubsidyDocumentCategory(category) && VALIDITY[category] !== undefined;
}

/** The default "valid until" for a document dated `from`; undefined for a category that does not expire. */
export function defaultValidUntil(category: string, from: Date): Date | undefined {
  if (!isSubsidyDocumentCategory(category)) return undefined;
  return VALIDITY[category]?.(from);
}
